import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { PersonDto } from 'dtos'
import { PersonHREmploymentInformationDto } from 'dtos/personHREmploymentInformationDto'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { formatDate } from 'utils'
import EmploymentInformationAddEditDialog from './EmploymentInformationAddEditDialog'

interface HREmploymentInformationCardProps {
  person: PersonDto
}

export default function HREmploymentInformationCard({ person }: HREmploymentInformationCardProps) {
  const { permissions } = useAuth()

  const [employmentInformationAddEditDialogOpen, setEmploymentInformationAddEditDialogOpen] = useState<boolean>(false)
  const [isGettingPersonHREmploymentInformation, setIsGettingPersonHREmploymentInformation] = useState<boolean>(false)
  const [isSavingPersonHREmploymentInformation, setIsSavingPersonHREmploymentInformation] = useState<boolean>(false)
  const [personHREmploymentInformation, setPersonHREmploymentInformation] = useState<PersonHREmploymentInformationDto>(
    new PersonHREmploymentInformationDto()
  )

  useEffect(() => {
    if (person.personGuid) {
      setIsGettingPersonHREmploymentInformation(true)
      controllers
        .getPersonHREmploymentInformation(person.personGuid)
        .then(response => {
          setPersonHREmploymentInformation(response.value)
        })
        .finally(() => {
          setIsGettingPersonHREmploymentInformation(false)
        })
    }
  }, [person])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <EmploymentInformationAddEditDialog
          initialValues={personHREmploymentInformation}
          loading={isSavingPersonHREmploymentInformation}
          onClose={() => {
            setEmploymentInformationAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingPersonHREmploymentInformation(true)
            controllers
              .updatePersonHREmploymentInformation(person.personGuid!, values)
              .then(response => {
                enqueueSnackbar('HR/Employment Information Saved Successfully!', { variant: 'success' })
                setPersonHREmploymentInformation(response.value)
                setEmploymentInformationAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsSavingPersonHREmploymentInformation(false)
              })
          }}
          open={employmentInformationAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  HR/Employment Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setEmploymentInformationAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingPersonHREmploymentInformation && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingPersonHREmploymentInformation && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Start Date'
                    value={
                      personHREmploymentInformation.employmentStartDate
                        ? formatDate(personHREmploymentInformation.employmentStartDate)
                        : ''
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='End Date'
                    value={
                      personHREmploymentInformation.employmentEndDate
                        ? formatDate(personHREmploymentInformation.employmentEndDate)
                        : ''
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Birthday'
                    value={personHREmploymentInformation.birthday ? formatDate(personHREmploymentInformation.birthday) : ''}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**HR/Employment Information Card:**

This is only displayed if the person is a current or past employee.

**EDIT Button:**

Clicking the EDIT button will open the Edit HR/Employment Information Dialog.

**Start Date:**

Displays the Employee Start date as entered in the Edit HR/Employment Information Dialog.

If no value has been entered for the Start Date, a “-” will be displayed.

**End Date:**

Displays the Employee End date as entered in the Edit HR/Employment Information Dialog.

If no value has been entered for the End Date, a “-” will be displayed.

**Birthday:**

Displays the Employee Birthday as entered in the Edit HR/Employment Information Dialog.

If no value has been entered for the Birthday, a “-” will be displayed.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
